import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Logo } from '@/components/shared/Logo';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/features/auth/store/authStore';
import { 
  LayoutDashboard, 
  Layers,
  FolderTree, 
  HelpCircle, 
  Users, 
  Gamepad2, 
  BarChart3,
  LifeBuoy,
  Menu,
  X,
  LogOut,
  Loader2
} from 'lucide-react';
export const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user, isLoggingOut } = useAuthStore();

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'لوحة التحكم', exact: true },
    { path: '/admin/sections', icon: Layers, label: 'الأقسام' },
    { path: '/admin/categories', icon: FolderTree, label: 'الفئات' },
    { path: '/admin/questions', icon: HelpCircle, label: 'الأسئلة' },
    { path: '/admin/users', icon: Users, label: 'المستخدمين' },
    { path: '/admin/games', icon: Gamepad2, label: 'الألعاب' },
    { path: '/admin/lifelines', icon: LifeBuoy, label: 'وسائل المساعدة' },
    { path: '/admin/reports', icon: BarChart3, label: 'التقارير' },
  ];

  const isActive = (path: string, exact: boolean = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-primary flex">
      {/* Sidebar */}
      <aside
        className={`${
          sidebarOpen ? 'w-64' : 'w-0'
        } bg-accent-dark text-primary transition-all duration-300 overflow-hidden flex flex-col`}
      >
        {/* Logo Section */}
        <div className="p-6 border-b border-accent-greenDark/30 bg-white">
          <Logo />
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path, item.exact);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                  active
                    ? 'bg-secondary text-white shadow-lg'
                    : 'text-primary/80 hover:bg-accent-greenDark/20 hover:text-primary'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-accent-greenDark/30">
          <div className="mb-3 px-4 py-2 bg-accent-greenDark/20 rounded-lg">
            <p className="text-sm text-primary/90 font-medium">{user?.displayName || user?.username}</p>
            <p className="text-xs text-primary/70">{user?.email}</p>
            <p className="text-xs text-accent-greenDark font-medium">{user?.role}</p>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="w-full justify-start text-primary/80 hover:text-primary hover:bg-accent-red/20 disabled:opacity-50"
          >
            {isLoggingOut ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <LogOut className="w-4 h-4 mr-2" />
            )}
            {isLoggingOut ? 'جاري الخروج...' : 'تسجيل الخروج'}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-primary border-b-2 border-accent-greenDark shadow-sm">
          <div className="px-6 py-4 flex items-center justify-between">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-accent-dark">لوحة تحكم الإدارة</h1>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto bg-accent-lightGray/30 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
